import React, { FunctionComponent, ReactElement, memo } from 'react'
import Grid from '@sport1/react-elements/Grid'
import SmallBodyFont from '../../../Theme/Fonts/SmallBody'
import { TestID } from '../../../utils/PropTypesDefinitions'
import TickerPlayer, { TickerPlayerProps } from '../TickerPlayer'
import {
    YellowCard,
    SecondYellowCard,
    RedCard,
    Substitution,
    Whistle,
    VideoProof,
    Goal,
} from '../HelperClass/Icons'
import { RequireAtLeastOne } from '../../../utils/types/requireAtLeastOne'
import { mapSpaceValue } from '../../../Layouts/LayoutUtils'
import { Spacing } from '../../../Layouts/LayoutTypes'
import BasicText from '../../../Theme/Fonts/BasicText'

export type EventIconTypes =
    | 'YELLOW_CARD'
    | 'RED_CARD'
    | 'SECOND_YELLOW_CARD'
    | 'SUBSTITUTION'
    | 'WHISTLE'
    | 'VIDEO_PROOF'
    | 'GOAL'

export type Headline = {
    text: string
    icon?: EventIconTypes
}

export type Time = {
    main: string
    addedTime?: string
}

type Props = {
    hideLine?: boolean
    timeProps?: Time
    headlineProps?: Headline
    text?: string
    tickerPlayers?: TickerPlayerProps[]
    textSpaceBottom?: Spacing
} & TestID

export type TickerCommentaryProps = RequireAtLeastOne<Props, 'headlineProps' | 'text'>

export const mapTypeToIcon = (iconType: EventIconTypes): ReactElement | void => {
    if (iconType === 'YELLOW_CARD') {
        return <YellowCard fontSize="FS14" />
    } else if (iconType === 'RED_CARD') {
        return <RedCard fontSize="FS14" />
    } else if (iconType === 'SECOND_YELLOW_CARD') {
        return <SecondYellowCard fontSize="FS14" />
    } else if (iconType === 'SUBSTITUTION') {
        return <Substitution fontSize="FS14" />
    } else if (iconType === 'WHISTLE') {
        return <Whistle fontSize="FS14" />
    } else if (iconType === 'GOAL') {
        return <Goal fontSize="FS14" />
    } else if (iconType === 'VIDEO_PROOF') {
        return <VideoProof fontSize="FS14" />
    }
}

const TickerCommentary: FunctionComponent<TickerCommentaryProps> = ({
    timeProps,
    hideLine,
    headlineProps,
    text,
    tickerPlayers,
    textSpaceBottom = 'standard',
    testID,
}): ReactElement => {
    let leftMinHeight
    if (timeProps && !hideLine) {
        if (timeProps.addedTime) {
            leftMinHeight = 56
        } else {
            leftMinHeight = 36
        }
    }
    return (
        <Grid noExpand horizontal backgroundColor="pearl" testID={testID}>
            <Grid noExpand minHeight={leftMinHeight} width={!hideLine ? 38 : 16} centerHorizontal>
                {timeProps ? (
                    <SmallBodyFont bold color="onyx-4">
                        {timeProps.main}
                    </SmallBodyFont>
                ) : null}
                {timeProps && timeProps.addedTime ? (
                    <SmallBodyFont bold color="onyx-4">
                        {timeProps.addedTime}
                    </SmallBodyFont>
                ) : null}
                {!hideLine ? <Grid width={1} backgroundColor="moon-3" /> : null}
            </Grid>
            <Grid paddingRight={2} paddingBottom={mapSpaceValue(textSpaceBottom)}>
                {headlineProps ? (
                    <Grid minHeight={20} noExpand width="100%" horizontal>
                        <Grid minWidth={0}>
                            <BasicText fontSize="FS16" lineHeight="LH22" bold>
                                {headlineProps.text}
                            </BasicText>
                        </Grid>
                        {headlineProps.icon ? (
                            <Grid
                                noExpand
                                centerHorizontal
                                minWidth={20}
                                paddingTop={2}
                                paddingX={1}
                            >
                                {mapTypeToIcon(headlineProps.icon)}
                            </Grid>
                        ) : null}
                    </Grid>
                ) : null}
                {text ? (
                    <BasicText paddingRight={4} fontSize="FS16" lineHeight="LH22" color="onyx-5">
                        {text}
                    </BasicText>
                ) : null}
                {tickerPlayers?.map((props, i) => (
                    <TickerPlayer
                        key={`ticker_player_${i}`}
                        marginY={2}
                        marginRight={2}
                        {...props}
                    />
                ))}
            </Grid>
        </Grid>
    )
}

export default memo(TickerCommentary)
